import React, { useEffect, useMemo, useRef } from 'react';
import { createChart, CandlestickSeries, LineSeries } from 'lightweight-charts';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { useDateTimeFormatting } from '../utils/datetime';

export default function ChartTest() {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const rsiSeriesRef = useRef(null);

  // --- 1. Dummy Data ---------------------------------------------------------
  const priceData = [
    { time: '2025-07-24', open: 100, high: 105, low: 95, close: 102 },
    { time: '2025-07-25', open: 102, high: 108, low: 101, close: 106 },
    { time: '2025-07-28', open: 106, high: 110, low: 104, close: 108 },
    { time: '2025-07-29', open: 108, high: 112, low: 107, close: 111 },
    { time: '2025-07-30', open: 111, high: 115, low: 109, close: 113 },
    { time: '2025-07-31', open: 113, high: 118, low: 112, close: 117 },
  ];

  const rsiData = [
    { time: '2025-07-24', value: 55 },
    { time: '2025-07-25', value: 62 },
    { time: '2025-07-28', value: 58 },
    { time: '2025-07-29', value: 65 },
    { time: '2025-07-30', value: 70 },
    { time: '2025-07-31', value: 68 },
  ];

  // --- 2. Chart Setup --------------------------------------------------------
  const prefs = useUserPreferences();
  const { formatDate, formatDateTime } = useDateTimeFormatting();
  const toMs = (t) => {
    if (typeof t === 'number') return t > 1000000000000 ? t : t * 1000;
    if (t && typeof t === 'object' && typeof t.year === 'number') return new Date(t.year, (t.month || 1) - 1, t.day || 1).getTime();
    return undefined;
  };
  const axisFmt = useMemo(() => (t, tickType) => {
    const ms = toMs(t);
    if (!ms) return '';
    switch (tickType) {
      case 4: return formatDate?.(ms, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) || '';
      case 3: return formatDate?.(ms, { hour: '2-digit', minute: '2-digit' }) || '';
      case 2: return formatDate?.(ms, { month: 'short', day: 'numeric' }) || '';
      case 1: return formatDate?.(ms, { month: 'short', year: 'numeric' }) || '';
      case 0:
      default: return formatDate?.(ms, { year: 'numeric' }) || '';
    }
  }, [formatDate]);
  const crosshairFmt = useMemo(() => (t) => {
    const ms = toMs(t);
    if (!ms) return '';
    return formatDateTime?.(ms, { second: '2-digit' }) || '';
  }, [formatDateTime]);

  useEffect(() => {
    if (chartRef.current) return; // Prevent re-initialization

    const chart = createChart(chartContainerRef.current, {
      height: 600,
      layout: {
        background: { color: '#141414' },
        textColor: '#DDD',
      },
      grid: {
        vertLines: { color: '#444' },
        horzLines: { color: '#444' },
      },
      timeScale: {
        borderColor: '#444',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (t, tickType) => axisFmt(t, tickType),
      },
      localization: { locale: prefs?.locale || 'en-US', timeFormatter: (t) => crosshairFmt(t) },
    });
    chartRef.current = chart;

    // --- Price Series (Pane 0) ---
    const priceSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });
    priceSeries.setData(priceData);

    // --- RSI Series (Pane 1) ---
    // By specifying a pane index, we create a new pane for the RSI.
    const rsiSeries = chart.addSeries(LineSeries, {
      color: '#ff9900',
      lineWidth: 2,
    }, 1); // <-- Key change: pane index is the 3rd argument
    rsiSeries.setData(rsiData);
    rsiSeriesRef.current = rsiSeries;

    // --- Pane Configuration ---
    // Set a fixed height for the RSI pane
    const rsiPane = rsiSeries.getPane();
    rsiPane.setHeight(120);

    chart.timeScale().fitContent();

    // Clean-up
    return () => {
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, []);

  // Respond to preference changes
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions({
      localization: { locale: prefs?.locale || 'en-US', timeFormatter: (t) => crosshairFmt(t) },
      timeScale: { tickMarkFormatter: (t, tickType) => axisFmt(t, tickType) },
    });
  }, [prefs?.locale, prefs?.timezone, axisFmt, crosshairFmt]);

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        ref={chartContainerRef}
        style={{ flex: 1 }}
      />
    </div>
  );
}
